import Text from 'components/commons/text';
import { styled, CSS } from 'styles';
import typography from 'styles/typography';

const cardStyle: CSS = {
  display: 'flex',
  backgroundColor: '$white',
  boxShadow: '$shadow1',
  borderRadius: 8,
  justifyContent: 'space-between',
  cursor: 'pointer',
  flex: 1,
  padding: 12,
  '&:disabled': {
    backgroundColor: '$neutral1',
    cursor: 'not-allowed',
  },
  '@md': {
    px: 24,
    py: 16,
  },
};

export const SummaryContainer = styled('div', {
  flex: 1,
  display: 'flex',
  backgroundColor: '$secondary',
  justifyContent: 'center',
  px: 16,
  py: 24,
});

export const AddonsFooter = styled('div', {
  display: 'flex',
  flex: 1,
  padding: 16,
  justifyContent: 'center',
  alignItems: 'flex-start',
  backgroundColor: '$white',
  boxShadow: '0px -4px 10px rgba(21, 76, 152, 0.12)',
  position: 'fixed',
  bottom: 0,
  left: 0,
  right: 0,
});

export const Section = styled('div', {
  display: 'flex',
  flex: 2,
  flexDirection: 'column',
  alignItems: 'center',
  px: 16,
  py: 32,
});

export const SectionContent = styled('div', {
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  '@sm': {
    maxWidth: 576,
  },
  '@md': {
    maxWidth: 768,
  },
  '@lg': {
    maxWidth: 992,
  },
  '@xl': {
    maxWidth: 1200,
  },
});

export const FacilityCard = styled('button', cardStyle);

export const AddonsFooterContent = styled('div', {
  width: '100%',
  display: 'flex',
  justifyContent: 'space-between',
  '@sm': {
    maxWidth: 576,
  },
  '@md': {
    maxWidth: 768,
  },
  '@lg': {
    maxWidth: 992,
  },
  '@xl': {
    maxWidth: 1200,
  },
});

export const FacilityImageContainer = styled('div', {
  position: 'relative',
  borderRadius: 999,
  overflow: 'hidden',
  width: 70,
  height: 70,
  '@md': {
    width: 90,
    height: 90,
  },
});

export const AddFacilityTitleText = styled(Text, {
  ...typography.headline7,
  '@md': {
    ...typography.headline6,
  },
});

export const AddFacilityContentText = styled(Text, {
  ...typography.body5,
  '@md': {
    ...typography.body4,
  },
});

export const TotalPriceContainer = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  '& > *:first-child': {
    mb: 4,
  },
  '@md': {
    flexDirection: 'row',
    alignItems: 'center',
    '& > *:first-child': {
      mb: 0,
      mr: 16,
    },
  },
});

export const PriceText = styled(Text, {
  ...typography.headline5,
  '@md': {
    ...typography.headline4,
  },
});

export const PriceButton = styled('button', {
  display: 'flex',
  cursor: 'pointer',
  borderRadius: 4,
  '&:focus': {
    outlineColor: '#DCD4FF',
    outlineStyle: 'solid',
    outlineWidth: 'medium',
  },
  '&:hover': {
    backgroundColor: '$neutral1',
  },
  svg: {
    ml: 8,
    color: '$typeGray',
  },
});

export const PanelContentTitle = styled(Text, {
  ...typography.body2,
  '@md': {
    ...typography.body1,
  },
  fontWeight: '600 !important',
  mb: 16,
});

export const PanelContent = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  position: 'relative',
  padding: 16,
  height: '100%',
  overflowY: 'scroll',
  flex: 1,
  '@md': {
    px: 32,
    py: 24,
  },
});

export const PanelSummaryContainer = styled('div', {
  borderRadius: 8,
  backgroundColor: '$secondary',
  display: 'flex',
  flexDirection: 'column',
  padding: 16,
  '@md': {
    padding: 20,
  },
});

export const PanelFooterTotalText = styled(Text, {
  ...typography.body2,
  '@md': {
    ...typography.body1,
  },
  fontWeight: '600 !important',
});

export const PanelFooterPriceText = styled(Text, {
  ...typography.headline5,
  '@md': {
    ...typography.headline4,
  },
});

export const ItemTypeText = styled(Text, {
  ...typography.body4,
  '@md': {
    ...typography.body3,
  },
  fontWeight: '600 !important',
  variants: {
    isRed: {
      true: {
        color: '$error',
      },
    },
  },
});

export const ItemText = styled(Text, {
  ...typography.body4,
  '@md': {
    ...typography.body3,
  },
});

export const ItemBasePriceText = styled(Text, {
  color: '$typeGray',
  ...typography.body5,
  '@md': {
    ...typography.body4,
  },
});

export const PanelFacilityTitleText = styled(Text, {
  ...typography.headline7,
  '@md': {
    ...typography.headline5,
  },
});

export const FacilityIconContainer = styled('div', {
  mr: 16,
  svg: {
    width: 36,
    height: 36,
  },
  '@md': {
    svg: {
      width: 48,
      height: 48,
    },
  },
});

export const ImageBox = styled('button', {
  width: 100,
  height: 100,
  overflow: 'hidden',
  borderRadius: 8,
  position: 'relative',
  cursor: 'pointer',
  '@md': {
    width: 120,
    height: 120,
  },
});

export const StyledClassCard = styled('div', {
  ...cardStyle,
  mb: 32,
  cursor: 'default',
  variants: {
    disabled: {
      true: {
        backgroundColor: '$neutral1',
      },
    },
  },
});

export const styles = {
  totalText: {
    color: '$typeGray',
    ...typography.body4,
    '@md': {
      ...typography.body3,
    },
    fontWeight: '600 !important',
  },
  priceContainer: {
    alignItems: 'flex-start',
    '@xl': {
      alignItems: 'flex-end',
    },
  },
  rightSideContainer: {
    mt: 16,
    flexDirection: 'column',
    justifyContent: 'space-between',
    borderTop: '1px dashed $neutral4',
    paddingTop: 16,
    '@media(min-width: 390px)': {
      flexDirection: 'row',
    },
    '@xl': {
      paddingTop: 0,
      border: 'none',
      flexDirection: 'column',
      mt: 0,
      alignItems: 'flex-end',
    },
  },
  priceContent: {
    flexDirection: 'column',
    '@xl': {
      flexDirection: 'row',
    },
  },
  selectButton: {
    '@sm': {
      minWidth: 110,
    },
  },
  passengerVehicleContainer: {
    flexDirection: 'column',
    gap: 16,
    '@md': {
      gap: 0,
      flexDirection: 'row',
      flexWrap: 'wrap',
    },
  },
  bottomContainer: {
    flexDirection: 'column',
    '@xl': {
      flexDirection: 'row',
      flexWrap: 'wrap',
    },
  },
  breakPoints: {
    width: '100%',
    '@sm': {
      maxWidth: 576,
    },
    '@md': {
      maxWidth: 768,
    },
    '@lg': {
      maxWidth: 992,
    },
    '@xl': {
      maxWidth: 1200,
    },
  },
} as const;
